import React, { useState, useRef, useEffect } from 'react';
import { Modal, Button, Spinner, Alert } from 'react-bootstrap';


const PDFViewer = ({ show, onHide, pdfBlob, title = 'PDF Document' }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const iframeRef = useRef(null);
  const loadingTimeoutRef = useRef(null);

  useEffect(() => {
    if (show && pdfBlob && iframeRef.current) {
      setLoading(true);
      setError(null);

      // Create a URL for the blob
      const url = URL.createObjectURL(pdfBlob);
      
      // Set timeout for iframe loading (30 seconds)
      loadingTimeoutRef.current = setTimeout(() => {
        if (loading) {
          setLoading(false);
          setError('PDF is taking too long to load. Please try downloading instead.');
        }
      }, 30000);

      // Set the iframe src to the blob URL
      iframeRef.current.src = url;
      
      // Set up a listener for when the iframe loads
      const handleLoad = () => {
        clearTimeout(loadingTimeoutRef.current);
        setLoading(false);
        setError(null);
      };

      // Set up a listener for iframe errors
      const handleError = () => {
        clearTimeout(loadingTimeoutRef.current);
        setLoading(false);
        setError('Failed to load PDF. Please try downloading instead.');
      };
      
      iframeRef.current.addEventListener('load', handleLoad);
      iframeRef.current.addEventListener('error', handleError);
      
      // Clean up function
      return () => {
        clearTimeout(loadingTimeoutRef.current);
        URL.revokeObjectURL(url);
        if (iframeRef.current) {
          iframeRef.current.removeEventListener('load', handleLoad);
          iframeRef.current.removeEventListener('error', handleError);
        }
      };
    }
  }, [show, pdfBlob]);

  // Reset loading state when modal is closed
  useEffect(() => {
    if (!show) {
      setLoading(true);
      setError(null);
      clearTimeout(loadingTimeoutRef.current);
    }
  }, [show]);

  // Handle download
  const handleDownload = () => {
    if (pdfBlob) {
      try {
        const url = URL.createObjectURL(pdfBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${title.replace(/\s+/g, '_')}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (err) {
        console.error('Download error:', err);
        setError('Failed to download PDF. Please try again.');
      }
    }
  };

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      size="xl" 
      centered 
      dialogClassName="pdf-viewer-modal modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ height: '80vh', position: 'relative' }} className='pdf-document-container'>
        {/* Loading Spinner */}
        {loading && !error && (
          <div 
            style={{ 
              position: 'absolute', 
              top: '50%', 
              left: '50%', 
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
              zIndex: 10
            }}
          >
            <Spinner animation="border" role="status" variant="primary">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-2">Loading PDF...</p>
            <p className="text-muted" style={{ fontSize: '12px' }}>
              If this takes too long, please try downloading the PDF instead.
            </p>
          </div>
        )}

        {/* Error Alert */}
        {error && (
          <div 
            style={{ 
              position: 'absolute', 
              top: '50%', 
              left: '50%', 
              transform: 'translate(-50%, -50%)',
              width: '80%',
              maxWidth: '500px',
              zIndex: 10
            }}
          >
            <Alert variant="danger" className="text-center">
              <Alert.Heading>Unable to Load PDF</Alert.Heading>
              <p>{error}</p>
              <hr />
              <div className="d-flex justify-content-center gap-2">
                <Button 
                  variant="outline-danger" 
                  onClick={handleDownload}
                >
                  Download PDF Instead
                </Button>
                <Button 
                  variant="outline-secondary" 
                  onClick={onHide}
                >
                  Close
                </Button>
              </div>
            </Alert>
          </div>
        )}

        {/* PDF Iframe */}
        <iframe 
          ref={iframeRef}
          style={{ 
            width: '100%', 
            height: '100%', 
            border: 'none',
            display: (loading || error) ? 'none' : 'block'
          }}
          title="PDF Viewer"
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
        <Button 
          variant="primary" 
          onClick={handleDownload}
          disabled={!pdfBlob}
        >
          Download
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PDFViewer;